/**
 * Loading placeholder for a whole view (UX 1.0a, UI-alus §1 "Laadimine").
 *
 * - Nothing for the first 200 ms: a fast answer should not flash grey boxes.
 * - Then the content's shape (a card list, or a detail page), inside ONE
 *   named container with `aria-busy="true"`; the shapes are hidden from
 *   assistive tech, so a screen reader hears "Laadin…", not twenty blanks.
 * - It is a placeholder, not a state: the page replaces it with data, an
 *   `EmptyState` or an `ErrorWithRetry`. Never leave it up indefinitely.
 * - The skeleton pulse follows MUI's animation, which the 1.0d theme turns
 *   off under `prefers-reduced-motion`.
 */

import { Box, Card, CardContent, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ListSkeletonRows } from './ListSkeleton';
import { useDelayed } from './useDelayed';

export interface PageSkeletonProps {
  /** Shape to imitate. Default `list`. */
  variant?: 'list' | 'detail';
  /** Rows for `list`. Default 5. */
  count?: number;
  /** Translated accessible name. Defaults to "Laadin…". */
  label?: string;
  /** Default 200. */
  delayMs?: number;
}

function DetailShape() {
  return (
    <Stack spacing={2} aria-hidden>
      <Skeleton variant="text" width="45%" height={34} />
      <Card variant="outlined">
        <CardContent>
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="60%" />
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rounded" height={64} sx={{ mt: 1 }} />
        </CardContent>
      </Card>
    </Stack>
  );
}

export function PageSkeleton({ variant = 'list', count = 5, label, delayMs = 200 }: PageSkeletonProps) {
  const { t } = useTranslation();
  const visible = useDelayed(delayMs);
  if (!visible) return null;
  const name = label ?? t('common:ui.skeleton.loading', { defaultValue: 'Laadin…' });
  return (
    <Box role="group" aria-busy="true" aria-label={name} data-testid="page-skeleton">
      {variant === 'detail' ? <DetailShape /> : <ListSkeletonRows count={count} />}
    </Box>
  );
}
